import type { CreateCheckoutOutput } from '@gg/shared';

/**
 * Checkout state that must survive a reload and a return from a wallet sheet (invariant 7):
 * the selection, email, opt-ins and voucher of screens 01–02, and the PaymentIntent of screen 03 so a
 * reload of /pay reuses it instead of creating a second order.
 *
 * Kept in sessionStorage: it survives reloads and redirects in the same tab, and is gone
 * when the tab is closed (no email left behind on a shared device).
 */
export type CheckoutDraft = {
  formatIds: string[];
  email: string;
  acceptTerms: boolean;
  marketingOptIn: boolean;
  referralCode?: string;
  /** Voucher accepted by /vouchers/validate on screen 02 (P-03); re-validated at checkout. */
  voucherCode?: string;
  /** Cancellation insurance opt-in (P-19), not selected by default. */
  insurance: boolean;
  /** The insured person's name, asked once the insurance is ticked (decision 0025). */
  insuranceHolder?: { firstName: string; lastName: string };
  /** Team format (P-20): the name typed on screen 02 by the captain. */
  teamName?: string;
  /**
   * Teammate paying their place from an invitation link (P-21): the seat's join code and what
   * screen 02 shows for it (team, captain, locked price). Set by /teams/join/:code, cleared when
   * the visitor goes back to the format selection.
   */
  joinCode?: string;
  team?: {
    id: string;
    name: string;
    formatId: string;
    /** "Camille" or the captain's email; null when the team has no captain (refunded). */
    captain: string | null;
    priceCents: number;
    tierName: string;
  };
  /**
   * Adding a race from the profile (P-13): formats already bought are hidden and, while the
   * session lasts, screen 02 skips the email step (02b).
   */
  upsell?: boolean;
};

/** A created order, tied to the draft it was created from. */
export type PendingCheckout = CreateCheckoutOutput & {
  email: string;
  /** `draftKey()` of the draft that created it: a changed draft needs a new order. */
  key: string;
};

const DRAFT = 'gg_checkout_draft';
const PENDING = 'gg_checkout_pending';
/** Reuse a pending order only if its hold has at least this long left. */
const MIN_REMAINING_MS = 2 * 60 * 1000;

export const emptyDraft: CheckoutDraft = {
  formatIds: [],
  email: '',
  acceptTerms: false,
  /** Pre-ticked: the visitor unticks it to opt out. */
  marketingOptIn: true,
  insurance: false,
};

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null; // storage disabled (private mode, blocked cookies)
  }
}

function read<T>(key: string): T | null {
  try {
    const raw = storage()?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    storage()?.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or disabled storage: the checkout still works, it just won't survive a reload.
  }
}

export function loadDraft(): CheckoutDraft {
  return { ...emptyDraft, ...read<Partial<CheckoutDraft>>(DRAFT) };
}

export function saveDraft(patch: Partial<CheckoutDraft>): CheckoutDraft {
  const next = { ...loadDraft(), ...patch };
  write(DRAFT, next);
  return next;
}

/** What the order depends on. Opt-ins are included so a changed choice is sent to the API. */
export function draftKey(d: CheckoutDraft): string {
  return JSON.stringify([
    [...d.formatIds].sort(),
    d.email.trim().toLowerCase(),
    d.marketingOptIn,
    d.referralCode ?? null,
    d.voucherCode ?? null,
    d.insurance,
    d.insurance ? (d.insuranceHolder ?? null) : null,
    d.teamName?.trim() ?? null,
    d.joinCode ?? null,
  ]);
}

export function loadPending(now = Date.now()): PendingCheckout | null {
  const p = read<PendingCheckout>(PENDING);
  return p && new Date(p.expiresAt).getTime() > now ? p : null;
}

/** The pending order for this draft, if it is still worth paying (enough hold left). */
export function reusablePending(draft: CheckoutDraft, now = Date.now()): PendingCheckout | null {
  const p = loadPending(now);
  if (!p || p.key !== draftKey(draft)) return null;
  return new Date(p.expiresAt).getTime() - now > MIN_REMAINING_MS ? p : null;
}

export function savePending(p: PendingCheckout): void {
  write(PENDING, p);
}

/** After a successful payment: the next visit starts a fresh checkout. */
export function clearCheckout(): void {
  try {
    storage()?.removeItem(DRAFT);
    storage()?.removeItem(PENDING);
  } catch {
    // ignore
  }
}
